import type { PaperOrder, PaperPosition, TradeLogEntry, PendingOrder, BacktestSessionData, BacktestSessionMetadata } from '@shared/types';
import { CONFIG } from '../config';
import { apiFetch } from './api';

// Use functions to get URLs at runtime, not build-time frozen values
const getApiBaseUrl = () => `${CONFIG.getBackendUrl()}/api/paper-trading`;
const getBacktestApiBaseUrl = () => `${CONFIG.getBackendUrl()}/api/backtest-sessions`;

export interface PaperTradingState {
  balance: number;
  equity: number;
  totalPnL: number;
  realizedPnL: number;
  dailyPnL: number;
  positions: PaperPosition[];
  orders: PaperOrder[];
  pendingOrders: PendingOrder[];
  tradeLog: TradeLogEntry[];
  stats: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    winRate: number;
    profitFactor: number;
    largestWin: number;
    largestLoss: number;
  };
  lastUpdated: number;
}

export interface TradeLogQueryOptions {
  symbol?: string;
  startDate?: number;
  endDate?: number;
  limit?: number;
}

/**
 * API client for paper trading persistence
 */
class PaperTradingAPI {
  /**
   * Load complete trading state from server
   */
  async loadState(): Promise<PaperTradingState> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/state`);
      if (!response.ok) {
        throw new Error(`Failed to load state: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('💾 [API] Loaded state from server:', data);
      return data;
    } catch (error) {
      console.error('❌ [API] Failed to load state:', error);
      throw error;
    }
  }

  /**
   * Save trading state to server
   */
  async saveState(state: Partial<PaperTradingState>): Promise<void> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/save`, {
        method: 'POST',
        
        body: JSON.stringify(state),
      });
      if (!response.ok) {
        throw new Error(`Failed to save state: ${response.statusText}`);
      }
      console.log('💾 [API] Saved state to server');
    } catch (error) {
      console.error('❌ [API] Failed to save state:', error);
      throw error;
    }
  }

  /**
   * Add an order
   */
  async addOrder(order: PaperOrder): Promise<void> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/order`, {
        method: 'POST',
        
        body: JSON.stringify(order),
      });
      if (!response.ok) {
        throw new Error(`Failed to add order: ${response.statusText}`);
      }
      console.log('📝 [API] Added order:', order.id);
    } catch (error) {
      console.error('❌ [API] Failed to add order:', error);
      throw error;
    }
  }

  /**
   * Add trade log entry
   */
  async addTradeLogEntry(entry: TradeLogEntry): Promise<void> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/trade-log`, {
        method: 'POST',
        
        body: JSON.stringify(entry),
      });
      if (!response.ok) {
        throw new Error(`Failed to add trade log entry: ${response.statusText}`);
      }
      console.log('📝 [API] Added trade log entry:', entry.id);
    } catch (error) {
      console.error('❌ [API] Failed to add trade log entry:', error);
      throw error;
    }
  }

  /**
   * Get trade log with optional filtering
   */
  async getTradeLog(options?: TradeLogQueryOptions): Promise<TradeLogEntry[]> {
    try {
      const params = new URLSearchParams();
      if (options?.symbol) params.append('symbol', options.symbol);
      if (options?.startDate) params.append('startDate', options.startDate.toString());
      if (options?.endDate) params.append('endDate', options.endDate.toString());
      if (options?.limit) params.append('limit', options.limit.toString());

      const url = `${getApiBaseUrl()}/trade-log${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to get trade log: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('📝 [API] Loaded trade log:', data.count, 'entries');
      return data.logs;
    } catch (error) {
      console.error('❌ [API] Failed to get trade log:', error);
      throw error;
    }
  }

  /**
   * Reset account (resets everything including backtest data)
   */
  async reset(initialBalance: number = 100000): Promise<void> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/reset`, {
        method: 'POST',
        
        body: JSON.stringify({ initialBalance }),
      });
      if (!response.ok) {
        throw new Error(`Failed to reset account: ${response.statusText}`);
      }
      console.log('🔄 [API] Reset account with balance:', initialBalance);
    } catch (error) {
      console.error('❌ [API] Failed to reset account:', error);
      throw error;
    }
  }

  /**
   * Reset live trading account only (preserves backtest data)
   */
  async resetLiveOnly(initialBalance: number = 100000): Promise<void> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/reset-live`, {
        method: 'POST',
        
        body: JSON.stringify({ initialBalance }),
      });
      if (!response.ok) {
        throw new Error(`Failed to reset live account: ${response.statusText}`);
      }
      console.log('🔄 [API] Reset live trading account with balance:', initialBalance);
    } catch (error) {
      console.error('❌ [API] Failed to reset live account:', error);
      throw error;
    }
  }

  /**
   * Get summary statistics
   */
  async getSummary(): Promise<{
    balance: number;
    equity: number;
    totalPnL: number;
    dailyPnL: number;
    totalTrades: number;
    winRate: number;
    tradeLogCount: number;
  }> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/summary`);
      if (!response.ok) {
        throw new Error(`Failed to get summary: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('📊 [API] Loaded summary:', data);
      return data;
    } catch (error) {
      console.error('❌ [API] Failed to get summary:', error);
      throw error;
    }
  }

  /**
   * Add a pending order
   */
  async addPendingOrder(order: PendingOrder): Promise<void> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/pending-order`, {
        method: 'POST',
        
        body: JSON.stringify(order),
      });
      if (!response.ok) {
        throw new Error(`Failed to add pending order: ${response.statusText}`);
      }
      console.log('📝 [API] Added pending order:', order.id);
    } catch (error) {
      console.error('❌ [API] Failed to add pending order:', error);
      throw error;
    }
  }

  /**
   * Remove a pending order
   */
  async removePendingOrder(id: string): Promise<void> {
    try {
      const response = await apiFetch(`${getApiBaseUrl()}/pending-order/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Failed to remove pending order: ${response.statusText}`);
      }
      console.log('🗑️ [API] Removed pending order:', id);
    } catch (error) {
      console.error('❌ [API] Failed to remove pending order:', error);
      throw error;
    }
  }

  // ==================== Backtest Session Management ====================

  /**
   * Create a new backtest session
   */
  async createBacktestSession(metadata: Omit<BacktestSessionMetadata, 'id' | 'createdAt' | 'status'>): Promise<BacktestSessionMetadata> {
    try {
      const response = await apiFetch(`${getBacktestApiBaseUrl()}`, {
        method: 'POST',
        
        body: JSON.stringify(metadata),
      });
      if (!response.ok) {
        throw new Error(`Failed to create backtest session: ${response.statusText}`);
      }
      const session = await response.json();
      console.log('🎬 [API] Created backtest session:', session.id);
      return session;
    } catch (error) {
      console.error('❌ [API] Failed to create backtest session:', error);
      throw error;
    }
  }

  /**
   * Save backtest session data
   */
  async saveBacktestSession(sessionId: string, data: Partial<PaperTradingState>): Promise<void> {
    try {
      const response = await apiFetch(`${getBacktestApiBaseUrl()}/${sessionId}`, {
        method: 'PUT',
        
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(`Failed to save backtest session: ${response.statusText}`);
      }
      console.log('💾 [API] Saved backtest session:', sessionId);
    } catch (error) {
      console.error('❌ [API] Failed to save backtest session:', error);
      throw error;
    }
  }

  /**
   * Load a backtest session
   */
  async loadBacktestSession(sessionId: string): Promise<BacktestSessionData> {
    try {
      const response = await apiFetch(`${getBacktestApiBaseUrl()}/${sessionId}`);
      if (!response.ok) {
        throw new Error(`Failed to load backtest session: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('📂 [API] Loaded backtest session:', sessionId);
      return data;
    } catch (error) {
      console.error('❌ [API] Failed to load backtest session:', error);
      throw error;
    }
  }

  /**
   * Get all backtest sessions
   */
  async listBacktestSessions(): Promise<BacktestSessionMetadata[]> {
    try {
      const response = await apiFetch(`${getBacktestApiBaseUrl()}`);
      if (!response.ok) {
        throw new Error(`Failed to list backtest sessions: ${response.statusText}`);
      }
      const sessions = await response.json();
      console.log('📋 [API] Listed', sessions.length, 'backtest sessions');
      return sessions;
    } catch (error) {
      console.error('❌ [API] Failed to list backtest sessions:', error);
      throw error;
    }
  }

  /**
   * Complete a backtest session
   */
  async completeBacktestSession(sessionId: string): Promise<void> {
    try {
      const response = await apiFetch(`${getBacktestApiBaseUrl()}/${sessionId}/complete`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(`Failed to complete backtest session: ${response.statusText}`);
      }
      console.log('✅ [API] Completed backtest session:', sessionId);
    } catch (error) {
      console.error('❌ [API] Failed to complete backtest session:', error);
      throw error;
    }
  }

  /**
   * Delete a backtest session
   */
  async deleteBacktestSession(sessionId: string): Promise<void> {
    try {
      const response = await apiFetch(`${getBacktestApiBaseUrl()}/${sessionId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Failed to delete backtest session: ${response.statusText}`);
      }
      console.log('🗑️ [API] Deleted backtest session:', sessionId);
    } catch (error) {
      console.error('❌ [API] Failed to delete backtest session:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const paperTradingAPI = new PaperTradingAPI();
